import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { ClientRecord, clientsApi } from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/catalog/Skeleton";

export default function Clients() {
  const [clients, setClients] = useState<ClientRecord[] | null>(null);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);

  async function load() { setClients(await clientsApi.list(q || undefined)); }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="clients-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Clients</p>
          <h1 className="font-display text-3xl sm:text-4xl">Clients</h1>
        </div>
        <div className="flex items-center gap-3">
          <Input data-testid="clients-search" placeholder="Search clients…" value={q}
            onChange={(e: any) => { setQ(e.target.value); }}
            onBlur={load} onKeyDown={(e: any) => e.key === "Enter" && load()} className="w-60" />
          <Button data-testid="clients-add-btn" onClick={() => setAdding(true)}
            className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10 px-5">
            <Plus className="w-4 h-4 mr-2" />New client
          </Button>
        </div>
      </div>

      {clients === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : clients.length === 0 ? (
        <div className="border border-border bg-bone/30 p-10 text-center" data-testid="clients-empty">
          <p className="font-display text-xl">No clients yet.</p>
          <p className="text-sm text-muted-foreground mt-2">Add a client to assign items, build looks, and share lookbooks.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
          {clients.map((c) => (
            <Link key={c._id} to={`/app/clients/${c._id}`}
              data-testid={`client-card-${c._id}`}
              className="block bg-background border border-border hover:border-oxblood/40 transition-colors p-5">
              <p className="font-display text-xl">{c.name}</p>
              {c.pronouns && <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">{c.pronouns}</p>}
              <p className="text-xs text-muted-foreground mt-3">{c.item_count} item{c.item_count === 1 ? "" : "s"}</p>
              {c.preferred_brands.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {c.preferred_brands.slice(0, 4).map((b) => (
                    <span key={b} className="text-[11px] px-2 py-0.5 border border-border">{b}</span>
                  ))}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      {adding && <ClientNewDialog onClose={() => setAdding(false)} onCreated={() => { setAdding(false); load(); }} />}
    </main>
  );
}

function ClientNewDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [pronouns, setPronouns] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [brands, setBrands] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      await clientsApi.create({
        name: name.trim(),
        pronouns: pronouns.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        preferred_brands: brands.split(",").map((s) => s.trim()).filter(Boolean),
        notes: notes.trim() || undefined,
      });
      onCreated();
    } catch (e: any) { setError(e?.message || "Could not create client."); }
    finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-charcoal/40 backdrop-blur-sm px-6" data-testid="client-new-dialog">
      <form onSubmit={submit} className="bg-background border border-border max-w-md w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-[0.22em] text-oxblood">— New client</p>
          <button type="button" onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <h3 className="font-display text-2xl">Add a new client</h3>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input data-testid="client-new-name" value={name} onChange={(e: any) => setName(e.target.value)} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Pronouns</Label><Input data-testid="client-new-pronouns" value={pronouns} onChange={(e: any) => setPronouns(e.target.value)} /></div>
            <div><Label>Phone</Label><Input data-testid="client-new-phone" value={phone} onChange={(e: any) => setPhone(e.target.value)} /></div>
          </div>
          <div><Label>Email</Label><Input type="email" data-testid="client-new-email" value={email} onChange={(e: any) => setEmail(e.target.value)} /></div>
          <div><Label>Preferred brands (comma-separated)</Label><Input data-testid="client-new-brands" value={brands} onChange={(e: any) => setBrands(e.target.value)} /></div>
          <div><Label>Notes</Label><textarea data-testid="client-new-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-border bg-background p-3 text-sm" /></div>
        </div>
        {error && <p className="text-sm text-oxblood">{error}</p>}
        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={!name.trim() || busy} data-testid="client-new-submit"
            className="flex-1 bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11">
            {busy ? "Creating…" : "Create client"}
          </Button>
          <Button type="button" onClick={onClose} className="bg-background border border-border hover:bg-bone/60 rounded-none h-11">Cancel</Button>
        </div>
      </form>
    </div>
  );
}
